<template>
  <fullscreen-dialog
    v-model="model"
    :title="
      isPurchaseOrder ? $t('warehouse.purchase_order') : $t('warehouse.request_quote')
    "
    icon="mdi-file-document-edit"
    color="green accent-4"
    :save-text="$t('order')"
    :disabled="!valid || !editedItem.groceries.length"
    :loading="loading"
    @save="save"
    :hide-footer="editedItem?.id"
  >
    <template v-slot:app-bar-actions>
      <warehouse-print-menu-button @print="print" />
    </template>

    <v-form ref="form" v-model="valid">
      <v-row>
        <v-col cols="12" lg="3" xl="2">
          <choose-warehouse-field
            hide-details
            v-model="editedItem.warehouse"
            color="white"
            :disabled="editedItem?.id"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <text-field
            :label="$t('warehouse.document_number')"
            v-model="editedItem.number"
            hide-details
            color="white"
            :disabled="editedItem?.id"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <date-picker
            v-model="editedItem.document_date"
            single-date
            color="white"
            :disabled="editedItem?.id"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <datetime-picker
            v-model="editedItem.offer_deadline"
            single-date
            :label="$t('warehouse.offers_deadline')"
            color="white"
            :disabled="editedItem?.id"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <datetime-picker
            v-model="editedItem.delivery_deadline"
            single-date
            :label="$t('warehouse.delivery_deadline')"
            color="white"
            :disabled="editedItem?.id"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <autocomplete
            v-model="editedItem.ordered_by"
            :items="users"
            item-text="fullName"
            item-value="id"
            :loading="loadingUsers"
            :label="$t('warehouse.ordered_by')"
            required
            :rules="[rules.required]"
            color="white"
            :disabled="editedItem?.id"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <choose-suplier-field
            hide-details
            v-model="editedItem.supplier"
            color="white"
            :disabled="editedItem?.id"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="4">
          <text-area
            :label="$t('note')"
            v-model="editedItem.notes"
            rows="1"
            :disabled="editedItem?.id"
            color="white"
          />
        </v-col>
      </v-row>
    </v-form>

    <warehouse-purchase-order-table
      v-if="selectedWarehouse"
      :groceries="editedItem.groceries"
      :warehouse="selectedWarehouse"
      :headers="headers"
      @removeGrocery="removeGrocery"
      :disabled="editedItem?.id"
    />
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasValidation from "@/mixins/hasValidation";
import hasPrinting from "@/mixins/hasPrinting";
import hasSelectedWarehouse from "@/mixins/warehouse/hasSelectedWarehouse";
import ChooseWarehouseField from "@/components/warehouse/field/ChooseWarehouseField.vue";
import WarehousePurchaseOrderTable from "@/components/warehouse/requirement/purchase/WarehousePurchaseOrderTable.vue";
import ChooseSuplierField from "@/components/warehouse/field/ChooseSuplierField.vue";
import { orderDocumentGrocery, requestOrderDocumentGrocery } from "@/models/warehouse";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import WarehousePrintMenuButton from "@/components/warehouse/buttons/WarehousePrintMenuButton.vue";

export default {
  name: "WarehouseDialogOrder",
  mixins: [hasModel, hasValidation, hasPrinting, hasSnackbar, hasSelectedWarehouse],
  components: {
    ChooseWarehouseField,
    WarehousePurchaseOrderTable,
    ChooseSuplierField,
    FullscreenDialog,
    WarehousePrintMenuButton,
  },
  props: {
    editedItem: {
      type: Object,
      default: () => orderDocumentGrocery,
    },
    isPurchaseOrder: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      search: "",
      valid: false,
      loadingUsers: false,
      datetime: new Date().toISOString(),
      loading: false,
    };
  },
  methods: {
    removeGrocery(grocery) {
      this.editedItem.groceries = this.editedItem.groceries.filter(
        (item) => grocery.id !== item.id
      );
    },
    print(withStock = false) {
      const headers = this.headers
        .filter((h) => h.value !== "actions")
        .map((h) =>
          withStock && h.value === "warehouse_stock" ? { ...h, print: undefined } : h
        );
      const items = this.editedItem.groceries.map((item, index) => ({
        ...item,
        num: index + 1,
      }));
      const orderedBy = this.users.find((user) => user.id === this.editedItem.ordered_by);
      this.printDocument({
        tables: [{ headers, items }],
        title: this.$t("request_for_quote_no") + " " + this.editedItem.number,
        beforeTitleHtml: `
            <div style="font-size: 16px;margin-top: 20px;">
              ${this.$t("date")}: ${this.localeDate(this.editedItem.document_date)}
            </div>
          `,
        company1: {
          ...this.company,
          printTitle: this.$t("warehouse.orderer"),
        },
        company2: {
          ...this.editedItem.supplier,
          printTitle: this.$t("supplier"),
        },
        afterTableHtml: `
            <div style="font-size: 16px;margin-top: 20px;">
              ${this.$t("warehouse.offers_deadline")}:<br/>
            ${this.$t("warehouse.delivery_deadline")}:
            </div>`,
        signature: {
          right: {
            title: this.$t("warehouse.for_orderer"),
            name: this.toLatin([orderedBy?.name, orderedBy?.lastname].join(" ")),
          },
        },
        hideDatetime: true,
      });
    },
    async save() {
      const params = {
        ...this.editedItem,
        warehouse_id: this.editedItem.warehouse?.id,
        warehouse: this.editedItem.warehouse,
        supplier_id: this.editedItem.supplier?.id || null, // Ensure supplier ID is passed or null
        groceries: this.editedItem.groceries.map((grocery) => ({
          ...grocery,
          warehouse_id: this.editedItem.warehouse?.id,
          warehouse: this.editedItem.warehouse,
          grocery_id: grocery.id,
          transaction_type: this.editedItem.document_type,
        })),
      };

      const request = warehouseService.addDocumentTransaction(params);

      try {
        this.loading = true;
        await this.handleRequest(request, {
          successMessage: this.isPurchaseOrder
            ? this.$t("purchase_order_created_successfully")
            : this.$t("request_quote_created_successfully"),
          errorMessagePrefix: this.$t("error"),
        });
        this.model = false; // Close dialog on success
        this.$emit("success"); // Emit success event
      } catch (error) {
        // Error is already handled by handleRequest,
        // but you can add additional component-specific error handling here if needed.
        console.error("Save operation failed:", error);
      } finally {
        this.loading = false; // Reset loading state
      }
    },
    async fetchUsers(refetch = false) {
      try {
        this.loadingUsers = true;
        await this.$store.dispatch("restaurantStore/dispatchUsers", {
          refetch,
        });
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        this.loadingUsers = false;
      }
    },
  },
  computed: {
    users() {
      return this.$store.state.restaurantStore.users.map((user) => ({
        ...user,
        fullName: [user.name, user.lastname].map((name) => this.toLatin(name)).join(" "),
      }));
    },
    company() {
      return this.$store.state.restaurantStore;
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", width: "100px" },
        {
          text: this.$t("code"),
          align: "start",
          value: "code",
          width: "150px",
        },
        { text: this.$t("title"), value: "name" },
        { text: this.$t("quantity"), value: "quantity", align: "right", width: "200px" },
        {
          text: this.$t("warehouse.warehouse_status"),
          value: "warehouse_stock",
          print: false,
          align: "right",
          width: "200px",
        },
        { text: this.$t("unit_of_measurment"), value: "measure", width: "200px" },
        { text: "", value: "actions", width: "100px" },
      ];
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
  },
  watch: {
    model: {
      handler(newVal) {
        if (!this.users.length) {
          this.fetchUsers();
        }
        if (newVal) {
          if (!this.editedItem.id) {
            let note = `Po osnovu trebovanja za period od ${this.filters.dates.from} do ${this.filters.dates.to}`;
            if (this.filters.dates.from === this.filters.dates.to) {
              note = `Po osnovu trebovanja za ${this.filters.dates.from}`;
            }
            this.editedItem.notes = this.translate(note);
          }
        }
      },
      immediate: true,
    },
    isPurchaseOrder: {
      handler() {
        if (this.editedItem.id) return;
        this.editedItem.document_type = this.isPurchaseOrder
          ? orderDocumentGrocery.document_type
          : requestOrderDocumentGrocery.document_type;
      },
      immediate: true,
    },
  },
};
</script>
